import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, BookOpen, Clock, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { publicService, UtilityPageDetail, PublicCourse, UtilityPageSummary } from '../services/publicService';
import { UtilityBridge } from '../components/UtilityBridge';
import { SlideCanvas } from '../components/course-builder/slides/SlideCanvas';
import { isSlideBasedContent } from '../components/course-builder/slides/slideTypes';
import { themes, Slide, SlideData, Theme } from '../components/course-builder/slides/slideTypes';
import { useDocumentMeta } from '../lib/useDocumentMeta';

export const UtilityPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<UtilityPageDetail | null>(null);
  const [parentCourse, setParentCourse] = useState<PublicCourse | null>(null);
  const [relatedPages, setRelatedPages] = useState<UtilityPageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  useEffect(() => {
    if (!slug) return;
    setIsLoading(true);
    setCurrentSlideIndex(0);

    publicService.getUtilityPageBySlug(slug)
      .then(async (data) => {
        setPage(data);
        if (data?.utilityMetadata.parentCourseId) {
          const [course, related] = await Promise.all([
            publicService.getParentCourse(data.utilityMetadata.parentCourseId),
            publicService.getUtilityPagesByCourse(data.utilityMetadata.parentCourseId),
          ]);
          setParentCourse(course);
          // Filter out current page from related
          setRelatedPages(related.filter(p => p.slug !== slug));
        }
      })
      .catch(err => console.error('Failed to load utility page:', err))
      .finally(() => setIsLoading(false));
  }, [slug]);

  // Extract slides from lesson content
  const slides = useMemo(() => {
    if (!page) return [];
    const allSlides: Array<{ slide: Slide; theme: Theme; lessonTitle: string }> = [];

    for (const lesson of page.lessonContent) {
      const content = lesson.content;
      if (isSlideBasedContent(content)) {
        const lessonTheme = themes[content.theme] || themes['modern-light'];
        for (const slide of content.slides.sort((a, b) => a.order - b.order)) {
          allSlides.push({ slide, theme: lessonTheme, lessonTitle: lesson.title });
        }
      }
    }
    return allSlides;
  }, [page]);

  const totalSlides = slides.length;
  const currentSlide = slides[currentSlideIndex];
  const progress = totalSlides > 0 ? ((currentSlideIndex + 1) / totalSlides) * 100 : 0;

  // SEO meta tags
  useDocumentMeta({
    title: page?.utilityMetadata.seoTitle || page?.title,
    description: page?.utilityMetadata.seoDescription || page?.description,
    ogTitle: page?.utilityMetadata.seoTitle || page?.title,
    ogDescription: page?.utilityMetadata.seoDescription || page?.description,
    ogImage: page?.utilityMetadata.ogImage || page?.thumbnailUrl,
    ogType: 'article',
    canonicalUrl: page ? `https://thinkshow.co.za/learn-free/${page.slug}` : undefined,
    structuredData: page ? {
      '@context': 'https://schema.org',
      '@type': page.utilityMetadata.schemaType || 'HowTo',
      name: page.utilityMetadata.seoTitle || page.title,
      description: page.utilityMetadata.seoDescription || page.description,
      image: page.utilityMetadata.ogImage || page.thumbnailUrl,
      ...(page.utilityMetadata.schemaType === 'HowTo' ? {
        step: slides.map((s, i) => ({
          '@type': 'HowToStep',
          position: i + 1,
          name: (s.slide.data as any).headline || (s.slide.data as any).heading || s.lessonTitle,
        })),
      } : {}),
    } : undefined,
  });

  const goNext = () => {
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(i => i + 1);
    }
  };

  const goPrev = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(i => i - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentSlideIndex, totalSlides]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-primary-600" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <BookOpen className="h-12 w-12 text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Guide not found</h2>
        <p className="text-gray-500 mb-6">This free guide may have been moved or removed.</p>
        <Link to="/learn-free" className="text-primary-600 font-semibold hover:underline">
          Browse all free guides
        </Link>
      </div>
    );
  }

  const isLastSlide = currentSlideIndex === totalSlides - 1;

  return (
    <div className="pb-20">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link
              to="/learn-free"
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">All Guides</span>
            </Link>
            <div className="text-center">
              <h1 className="text-sm font-semibold text-gray-900 truncate max-w-[200px] sm:max-w-md">
                {page.title}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Clock className="h-3.5 w-3.5" />
              <span>{page.estimatedMinutes} min</span>
            </div>
          </div>
          {/* Progress bar */}
          <div className="h-0.5 bg-gray-100 -mx-4 sm:-mx-6 lg:-mx-8">
            <motion.div
              className="h-full bg-primary-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Slide Viewer */}
      {totalSlides > 0 && currentSlide && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="relative">
            {/* Slide Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlideIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="w-full"
              >
                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                  <SlideCanvas
                    slide={currentSlide.slide}
                    theme={currentSlide.theme}
                    isEditing={false}
                    onUpdate={() => {}}
                  />
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button
              onClick={goPrev}
              disabled={currentSlideIndex === 0}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-14 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-all"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={goNext}
              disabled={isLastSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-14 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-all"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlideIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  idx === currentSlideIndex
                    ? 'bg-primary-600 w-6'
                    : idx < currentSlideIndex
                    ? 'bg-primary-300'
                    : 'bg-gray-200'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>

          {/* Slide counter */}
          <p className="text-center text-xs text-gray-400 mt-2">
            {currentSlideIndex + 1} of {totalSlides}
          </p>
        </div>
      )}

      {/* Empty state if no slides */}
      {totalSlides === 0 && (
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Coming Soon</h2>
          <p className="text-gray-500">This guide is being prepared. Check back soon!</p>
        </div>
      )}

      {/* Bridge CTA - shown prominently on last slide or below slides */}
      {parentCourse && (isLastSlide || totalSlides === 0) && (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <UtilityBridge
            parentCourse={parentCourse}
            ctaText={page.utilityMetadata.ctaText}
            ctaDescription={page.utilityMetadata.ctaDescription}
            leadMagnet={page.utilityMetadata.leadMagnet}
            relatedUtilityPages={relatedPages}
          />
        </div>
      )}

      {/* Always show bridge below fold if not on last slide */}
      {parentCourse && !isLastSlide && totalSlides > 0 && (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 opacity-60 hover:opacity-100 transition-opacity">
          <UtilityBridge
            parentCourse={parentCourse}
            ctaText={page.utilityMetadata.ctaText}
            ctaDescription={page.utilityMetadata.ctaDescription}
            relatedUtilityPages={relatedPages}
          />
        </div>
      )}
    </div>
  );
};
